import React from 'react';

import castArray from 'lodash/castArray';
import difference from 'lodash/difference';
import noop from 'lodash/noop';

import { getGroupPublic, getGroupAll } from '@lumapps/customer/ducks/selectors';
import { ListFeedParams } from '@lumapps/groups/api';
import { GROUPS_PER_FETCH } from '@lumapps/groups/constants';
import { useQueryGroupByIds } from '@lumapps/groups/hooks/useQueryGroupByIds';
import { useQueryGroupSearch } from '@lumapps/groups/hooks/useQueryGroupSearch';
import { Group } from '@lumapps/groups/types';
import { instanceSelector } from '@lumapps/instance/ducks/selectors';
import { useSelector } from '@lumapps/redux/react';
import { useTranslate } from '@lumapps/translations';
import { currentLanguageSelector } from '@lumapps/translations/ducks/selectors';
import { BaseLoadingStatus } from '@lumapps/utils/types/BaseLoadingStatus';

export interface UseGroupPickerFieldStateOptions {
    /** If true, search groups at the platform level (not just instance). */
    shouldSearchInPlatform?: boolean;
    /** If true, include the special "all group" in the picker. */
    shouldDisplayAllGroup?: boolean;
    /** If true, include the special "public group" in the picker. */
    shouldDisplayPublicGroup?: boolean;
    /** If true, the picker will display segments as well */
    shouldDisplaySegments?: boolean;
    /** Additional search parameters for group fetching. */
    searchParams?: Partial<ListFeedParams>;
    /** The current value(s) selected in the picker (Group object(s) or id(s)). */
    value?: Group | string | Array<Group | string>;
}

export interface GroupPickerFieldState {
    /** Set the current search value for filtering groups. */
    setSearchValue: (value: string) => void;
    /** Enable or disable the group search query. */
    setEnabled: (enabled: boolean) => void;
    /** Loading status of the group search query. */
    status: BaseLoadingStatus;
    /** Loading status for the group multi get query. */
    queryGroupsStatus: BaseLoadingStatus;
    /** Fetch the next page of groups (for infinite scroll). */
    fetchNextPage: () => void;
    /** Whether the picker should be disabled (e.g. while loading). */
    isDisabled: boolean;
    /** The list of groups to display in the picker. */
    groups: Group[];
    /** The selected groups (for multi-select). */
    multipleSelectedValue: Group[] | undefined;
    /** The selected group (for single-select). */
    singleSelectedValue: Group | undefined;
    /** currently applied search params */
    listSearchParams: ListFeedParams;
}

/**
 * useGroupPickerFieldState
 *
 * Custom React hook to manage the state and logic for a group picker field.
 * Handles searching, fetching, and selecting groups, including special handling for public and all-groups.
 *
 * @param options - Configuration options for the hook.
 * @returns State and actions for the group picker field.
 */
export const useGroupPickerFieldState = ({
    shouldSearchInPlatform = false,
    shouldDisplayPublicGroup = true,
    shouldDisplayAllGroup = true,
    shouldDisplaySegments = false,
    searchParams,
    value,
}: UseGroupPickerFieldStateOptions): GroupPickerFieldState => {
    // Selectors for special groups and context
    const allGroup = useSelector(getGroupAll);
    const publicGroup = useSelector(getGroupPublic);
    const language = useSelector(currentLanguageSelector);
    const instance = useSelector(instanceSelector);
    const { translateKey } = useTranslate();
    // State for search input and query enablement
    const [searchValue, setSearchValue] = React.useState<string>('');
    const [enabled, setEnabled] = React.useState(false);

    /**
     * Memoized search parameters for group fetching.
     * Handles exclusion of special groups and instance scoping.
     */
    const { listSearchParams, isRestrictionEmptyAfterExclusions } = React.useMemo(() => {
        const { exceptFeeds: callerExceptFeeds, ...restSearchParams } = searchParams || {};

        // Clone exceptFeeds to avoid mutating props
        const exceptFeeds = callerExceptFeeds ? [...callerExceptFeeds] : [];

        // Exclude allGroup if not displayed
        if (!shouldDisplayAllGroup && allGroup) {
            exceptFeeds.push(allGroup.uid || allGroup.id);
        }

        // Exclude publicGroup if not displayed
        if (!shouldDisplayPublicGroup && publicGroup) {
            exceptFeeds.push(publicGroup.uid || publicGroup.id);
        }

        const isRestricted = Boolean(restSearchParams.restrictToFeeds?.length);

        // The API rejects a whitelist (restrictToFeeds) combined with a blacklist (exceptFeeds):
        // when restricted, fold the exclusions into the whitelist and never send exceptFeeds.
        const params: ListFeedParams = {
            query: searchValue,
            maxResults: GROUPS_PER_FETCH.toString(),
            lang: language,
            ...restSearchParams,
            ...(isRestricted
                ? { restrictToFeeds: difference(restSearchParams.restrictToFeeds, exceptFeeds) }
                : { exceptFeeds }),
        };

        // If we don't search groups at the platform level, add the instance id to the request
        if (!shouldSearchInPlatform) {
            params.instance = restSearchParams.instance || instance.uid || instance.id;
        }

        if (shouldDisplaySegments) {
            params.excludeSegments = false; // Ensure segments are included if requested
        }

        return {
            listSearchParams: params,
            // A restriction that folds to empty must skip the query: an unrestricted request
            // would expose feeds outside the restriction (e.g. in a private space).
            isRestrictionEmptyAfterExclusions: isRestricted && params.restrictToFeeds?.length === 0,
        };
    }, [
        allGroup,
        instance.id,
        instance.uid,
        language,
        publicGroup,
        searchParams,
        shouldDisplaySegments,
        searchValue,
        shouldDisplayAllGroup,
        shouldDisplayPublicGroup,
        shouldSearchInPlatform,
    ]);

    // Normalize value to array and determine if we need to fetch group objects by id
    const valueArray = castArray(value).filter(Boolean);
    const isArrayOfStrings = Array.isArray(value) && value.every((v) => typeof v === 'string');
    const shouldFetchGroups = typeof value === 'string' || (valueArray.length > 0 && isArrayOfStrings);

    // Query for groups matching the search (skipped when the restriction is empty after exclusions)
    const query = useQueryGroupSearch(listSearchParams, { enabled: enabled && !isRestrictionEmptyAfterExclusions });
    // Query for selected groups by id (if value is string or array of strings)
    const queryGroups = useQueryGroupByIds(
        {
            uid: valueArray.map((v) => (typeof v === 'string' ? v : v.id)),
        },
        {
            enabled: shouldFetchGroups,
        },
    );

    // Disable the picker if fetching selected groups
    const isSelectDisabled = Boolean(valueArray.length && queryGroups.isLoading && shouldFetchGroups);

    /**
     * Compute the list of groups to display in the picker.
     * Handles inclusion/exclusion of special groups.
     */
    const groupsToDisplay = React.useMemo(() => {
        // Filter out special groups if needed
        const groups = query.items.filter((group: Group) => {
            if (group.id === allGroup?.id) {
                return false;
            }

            if (group.id === publicGroup?.id) {
                return false;
            }

            return true;
        });

        // Add special groups at the top if enabled
        if (shouldDisplayAllGroup && allGroup) {
            const allGroupName = translateKey(allGroup.name);

            if (allGroupName.toLowerCase().includes(searchValue.toLowerCase())) {
                groups.unshift(allGroup);
            }
        }

        if (shouldDisplayPublicGroup && publicGroup) {
            const publicGroupName = translateKey(publicGroup.name);

            if (publicGroupName.toLowerCase().includes(searchValue.toLowerCase())) {
                groups.unshift(publicGroup);
            }
        }

        return groups;
    }, [
        allGroup,
        publicGroup,
        query.items,
        searchValue,
        shouldDisplayAllGroup,
        shouldDisplayPublicGroup,
        translateKey,
    ]);

    let singleSelectedValue: Group | undefined;
    let multipleSelectedValue: Group[] | undefined;

    /**
     * If the value is a string or a single Group object, set the single selected value.
     * If it isn't a string and it is not an array, assume it's a single Group object.
     */
    if (typeof value === 'string') {
        singleSelectedValue = queryGroups.items?.[0];
    } else if (!Array.isArray(value)) {
        singleSelectedValue = value as Group;
    }

    /**
     * If the value is an array of strings or an array of Group objects,
     * set the multiple selected value.
     * If it isn't an array of strings and it is an array,
     * assume it's an array of Group objects.
     */
    if (isArrayOfStrings) {
        multipleSelectedValue = queryGroups.items?.filter(Boolean) as Group[];
    } else if (Array.isArray(value)) {
        multipleSelectedValue = value as Group[];
    }

    // When the search is skipped because the restriction is empty, report an idle status with no
    // results instead of the "loading" status a disabled query would report.
    let status = BaseLoadingStatus.initial;
    if (enabled) {
        status = isRestrictionEmptyAfterExclusions ? BaseLoadingStatus.idle : query.baseLoadingStatus;
    }

    return {
        setSearchValue,
        setEnabled,
        isDisabled: isSelectDisabled,
        status,
        queryGroupsStatus: shouldFetchGroups ? queryGroups.baseLoadingStatus : BaseLoadingStatus.initial,
        fetchNextPage: isRestrictionEmptyAfterExclusions ? noop : query.fetchNextPage,
        groups: groupsToDisplay,
        singleSelectedValue,
        multipleSelectedValue,
        listSearchParams,
    };
};
